/**
 * protocol_rule_engine.ts — placeholder for transport protocol selection.
 *
 * ## Design Intent
 *
 * The *actual* protocol decision lives on the **signaling server**:
 *   - Camera registers supported protocols on connect (e.g. jtt1078, webrtc).
 *   - Signaling server evaluates: camera capability + server load + user
 *     settings + session history, then returns ordered `transports[]` in
 *     `session_bootstrap`.
 *   - SDK picks the first transport in the list and uses it.
 *
 * This file provides:
 *   1. `ProtocolRuleContext` — the facts a client-side rule can observe.
 *   2. `ProtocolHint` — what the client can *suggest* to the server (via
 *      `quality_hint` or future `preferred_protocol` in `session_open`).
 *   3. `evaluateProtocolHint()` — returns `null` = "no client preference,
 *      let server decide". Override in integration layer when needed.
 *
 * ## When to add client-side logic here
 *
 * - User explicitly selects "force WebRTC" or "force JTT1078" in settings.
 * - App UI knows the device is on a LAN and wants to prefer P2P.
 * - Testing / debugging: force a specific transport.
 *
 * ## What NOT to put here
 *
 * - Network quality fallback decisions (server owns this via QoS telemetry).
 * - Camera capability queries (server owns this via device registry).
 * - Automatic retry across protocols (state machine in livekit_web owns this).
 */

export type ProtocolHint = 'p2p' | 'stream_socket' | null;

export interface ProtocolRuleContext {
  /** Device serial / ID. */
  deviceSerial: string;
  /** Channel ID. */
  channelId: string;
  /** User-visible quality preference, e.g. 'hd' | 'sd'. */
  qualityHint?: string;
  /**
   * Current network quality from QoS (may be 'unknown' at session open time).
   * Useful for pre-selecting transport if app has prior knowledge.
   */
  networkQuality?: 'good' | 'degraded' | 'poor' | 'unknown';
  /**
   * Any app-specific metadata (user settings, device flags, etc.) that an
   * integration might want to factor in.
   */
  meta?: Record<string, unknown>;
}

/**
 * Evaluate whether the app wants to *hint* a transport preference to the
 * signaling server.
 *
 * Returns:
 *   - `null`            → no preference, server decides (default, recommended)
 *   - `'p2p'`           → prefer WebRTC P2P
 *   - `'stream_socket'` → prefer JTT1078 WSS gateway
 *
 * Override this function in your integration layer to implement custom rules.
 * The returned value is sent as `preferred_protocol` in `session_open`
 * (not yet implemented in signaling server — field is ignored until server
 * supports it).
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function evaluateProtocolHint(_ctx: ProtocolRuleContext): ProtocolHint {
  // Default: no client-side preference — let the signaling server decide.
  return null;
}
